import React, { useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import './TrendsSection.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface TrendsSectionProps {
  doctorName: string;
  residentId?: number;
  globalTimeframe?: '1 week' | '1 month' | '2 months' | '6 months';
}

interface Insight {
  text: string;
  type: 'good' | 'bad';
  timeframe: '1 week' | '1 month' | '2 months' | '6 months';
}

interface ResidentInsights {
  [skill: string]: Insight[];
}

// Timeframe hierarchy for accumulation
const timeframeOrder = ['1 week', '1 month', '2 months', '6 months'];

const getTimeframeLabel = (tf: string): string => {
  switch(tf) {
    case '1 week': return '1 Week';
    case '1 month': return '1 Month';
    case '2 months': return '2 Months';
    case '6 months': return '6 Months';
    default: return tf;
  }
};

// Filter insights based on selected timeframe (cumulative)
const filterInsightsByTimeframe = (insights: Insight[], selectedTimeframe: string): Insight[] => {
  const selectedIndex = timeframeOrder.indexOf(selectedTimeframe);
  if (selectedIndex === -1) return insights;
  
  return insights.filter(insight => {
    const insightIndex = timeframeOrder.indexOf(insight.timeframe);
    return insightIndex <= selectedIndex;
  });
};

// Detailed insights per resident per skill with timeframe
const residentInsightsData: { [residentId: number]: ResidentInsights } = {
  1: { // Shani
    'Patient Care': [
      { text: 'Excellent bedside manner with pediatric patients', type: 'good', timeframe: '1 month' },
      { text: 'Consistent follow-up documentation', type: 'good', timeframe: '1 week' },
      { text: 'Delayed pain assessment in post-operative patients', type: 'bad', timeframe: '1 week' },
      { text: 'Missing family communication notes in 3 cases', type: 'bad', timeframe: '1 month' },
      { text: 'Improved patient satisfaction scores', type: 'good', timeframe: '2 months' },
      { text: 'Early recognition of deteriorating patient', type: 'good', timeframe: '6 months' },
    ],
    'History': [
      { text: 'Thorough allergy documentation', type: 'good', timeframe: '1 week' },
      { text: 'Did not document last echocardiogram results', type: 'bad', timeframe: '1 week' },
      { text: 'Insufficient detail in surgical history', type: 'bad', timeframe: '1 week' },
      { text: 'Did not document HbA1c in diabetic patients', type: 'bad', timeframe: '1 month' },
      { text: 'Consistent in asking about substance abuse', type: 'good', timeframe: '1 month' },
      { text: 'Missed travel history in infectious case', type: 'bad', timeframe: '2 months' },
      { text: 'Good family history documentation pattern', type: 'good', timeframe: '6 months' },
    ],
    'Medical Knowledge': [
      { text: 'Strong understanding of pediatric pharmacology', type: 'good', timeframe: '1 week' },
      { text: 'Excellent differential diagnosis in respiratory cases', type: 'good', timeframe: '1 month' },
      { text: 'Gaps in neonatal resuscitation protocols', type: 'bad', timeframe: '1 month' },
      { text: 'Good understanding of fluid management', type: 'good', timeframe: '2 months' },
      { text: 'Needs improvement in rare metabolic disorders', type: 'bad', timeframe: '6 months' },
    ],
    'Physical Examination': [
      { text: 'Did not check for meningeal signs in a febrile child', type: 'bad', timeframe: '1 week' },
      { text: 'Incomplete neurological exam in 2 cases', type: 'bad', timeframe: '1 week' },
      { text: 'Good auscultation technique documented', type: 'good', timeframe: '1 week' },
      { text: 'Missed hepatomegaly on abdominal exam', type: 'bad', timeframe: '1 month' },
      { text: 'Thorough skin examination for rashes', type: 'good', timeframe: '2 months' },
      { text: 'Improved cardiovascular exam skills', type: 'good', timeframe: '6 months' },
    ],
    'Professionalism': [
      { text: 'Always punctual for rounds', type: 'good', timeframe: '1 week' },
      { text: 'Excellent communication with nursing staff', type: 'good', timeframe: '1 month' },
      { text: 'Proactive in seeking feedback', type: 'good', timeframe: '2 months' },
      { text: 'Good role model for medical students', type: 'good', timeframe: '6 months' },
    ],
    'Clinical Reasoning': [
      { text: 'Good systematic approach to fever workup', type: 'good', timeframe: '1 week' },
      { text: 'Delayed recognition of sepsis in one case', type: 'bad', timeframe: '1 week' },
      { text: 'Did not consider drug interactions in elderly patient', type: 'bad', timeframe: '1 month' },
      { text: 'Improved pattern recognition skills', type: 'good', timeframe: '2 months' },
      { text: 'Good use of clinical decision rules', type: 'good', timeframe: '6 months' },
    ],
    'Interpersonal skills': [
      { text: 'Excellent rapport with anxious parents', type: 'good', timeframe: '1 week' },
      { text: 'Clear explanations of treatment plans', type: 'good', timeframe: '1 month' },
      { text: 'Sometimes interrupts during handoffs', type: 'bad', timeframe: '1 month' },
      { text: 'Good conflict resolution with team', type: 'good', timeframe: '2 months' },
    ],
    'Patient Management': [
      { text: 'Delayed discharge planning in 4 cases', type: 'bad', timeframe: '1 week' },
      { text: 'Incomplete medication reconciliation', type: 'bad', timeframe: '1 week' },
      { text: 'Did not coordinate with social work for complex discharge', type: 'bad', timeframe: '1 month' },
      { text: 'Good antibiotic stewardship practices', type: 'good', timeframe: '1 month' },
      { text: 'Improved care coordination over time', type: 'good', timeframe: '2 months' },
    ],
  },
  2: { // Hillel
    'Patient Care': [
      { text: 'Thorough daily assessments documented', type: 'good', timeframe: '1 week' },
      { text: 'Excellent pain management protocols', type: 'good', timeframe: '1 week' },
      { text: 'Proactive family updates', type: 'good', timeframe: '1 month' },
      { text: 'Exceptional care in critical cases', type: 'good', timeframe: '2 months' },
    ],
    'History': [
      { text: 'Comprehensive medication reconciliation', type: 'good', timeframe: '1 week' },
      { text: 'Always documents smoking status', type: 'good', timeframe: '1 week' },
      { text: 'Did not ask about previous asthma exacerbations', type: 'bad', timeframe: '1 month' },
      { text: 'Thorough travel history in infectious cases', type: 'good', timeframe: '2 months' },
      { text: 'Excellent social history documentation', type: 'good', timeframe: '6 months' },
    ],
    'Medical Knowledge': [
      { text: 'Strong cardiology knowledge base', type: 'good', timeframe: '1 week' },
      { text: 'Up-to-date on latest guidelines', type: 'good', timeframe: '1 month' },
      { text: 'Some gaps in rheumatology presentations', type: 'bad', timeframe: '2 months' },
      { text: 'Excellent evidence-based medicine application', type: 'good', timeframe: '6 months' },
    ],
    'Physical Examination': [
      { text: 'Excellent cardiovascular examination skills', type: 'good', timeframe: '1 week' },
      { text: 'Thorough skin examination for systemic disease', type: 'good', timeframe: '1 month' },
      { text: 'Consistent neurological assessments', type: 'good', timeframe: '2 months' },
    ],
    'Professionalism': [
      { text: 'Role model for junior residents', type: 'good', timeframe: '1 week' },
      { text: 'Excellent documentation timeliness', type: 'good', timeframe: '1 month' },
      { text: 'Occasionally late for morning handoff', type: 'bad', timeframe: '2 months' },
      { text: 'Outstanding teaching of medical students', type: 'good', timeframe: '6 months' },
    ],
    'Clinical Reasoning': [
      { text: 'Excellent diagnostic approach in complex cases', type: 'good', timeframe: '1 week' },
      { text: 'Strong evidence-based decision making', type: 'good', timeframe: '1 month' },
      { text: 'Considers cost-effectiveness in workups', type: 'good', timeframe: '2 months' },
    ],
    'Interpersonal skills': [
      { text: 'Excellent team collaboration', type: 'good', timeframe: '1 week' },
      { text: 'Clear communication during emergencies', type: 'good', timeframe: '1 month' },
      { text: 'Supportive of nursing staff concerns', type: 'good', timeframe: '2 months' },
    ],
    'Patient Management': [
      { text: 'Excellent discharge planning', type: 'good', timeframe: '1 week' },
      { text: 'Strong coordination with subspecialties', type: 'good', timeframe: '1 month' },
      { text: 'Proactive follow-up scheduling', type: 'good', timeframe: '2 months' },
    ],
  },
  3: { // Maayan
    'Patient Care': [
      { text: 'Good documentation of patient preferences', type: 'good', timeframe: '1 week' },
      { text: 'Delayed response to nursing calls in 2 instances', type: 'bad', timeframe: '1 week' },
      { text: 'Incomplete vital signs monitoring orders', type: 'bad', timeframe: '1 week' },
      { text: 'Improving patient communication', type: 'good', timeframe: '2 months' },
    ],
    'History': [
      { text: 'Did not document last echocardiogram', type: 'bad', timeframe: '1 week' },
      { text: 'Insufficient detail in surgical history', type: 'bad', timeframe: '1 week' },
      { text: 'Did not ask about smoking history', type: 'bad', timeframe: '1 week' },
      { text: 'Good family history documentation', type: 'good', timeframe: '1 month' },
      { text: 'Missed occupational exposure history', type: 'bad', timeframe: '2 months' },
    ],
    'Medical Knowledge': [
      { text: 'Needs improvement in electrolyte management', type: 'bad', timeframe: '1 week' },
      { text: 'Good understanding of antibiotic choices', type: 'good', timeframe: '1 month' },
      { text: 'Gaps in acute coronary syndrome protocols', type: 'bad', timeframe: '1 month' },
      { text: 'Improving pharmacology knowledge', type: 'good', timeframe: '2 months' },
    ],
    'Physical Examination': [
      { text: 'Incomplete fundoscopic exam in hypertensive patients', type: 'bad', timeframe: '1 week' },
      { text: 'Did not assess JVP in heart failure patient', type: 'bad', timeframe: '1 week' },
      { text: 'Good musculoskeletal examination technique', type: 'good', timeframe: '1 month' },
      { text: 'Missed peripheral edema assessment', type: 'bad', timeframe: '2 months' },
    ],
    'Professionalism': [
      { text: 'Good attitude toward learning', type: 'good', timeframe: '1 week' },
      { text: 'Documentation sometimes delayed', type: 'bad', timeframe: '1 week' },
      { text: 'Responsive to feedback', type: 'good', timeframe: '1 month' },
      { text: 'Improved punctuality over time', type: 'good', timeframe: '2 months' },
    ],
    'Clinical Reasoning': [
      { text: 'Needs more structured approach to differential', type: 'bad', timeframe: '1 week' },
      { text: 'Sometimes misses red flags in presentations', type: 'bad', timeframe: '1 week' },
      { text: 'Improving pattern recognition', type: 'good', timeframe: '1 month' },
      { text: 'Better use of clinical guidelines', type: 'good', timeframe: '2 months' },
    ],
    'Interpersonal skills': [
      { text: 'Good patient rapport', type: 'good', timeframe: '1 week' },
      { text: 'Needs improvement in difficult conversations', type: 'bad', timeframe: '1 month' },
      { text: 'Collaborative with team members', type: 'good', timeframe: '2 months' },
    ],
    'Patient Management': [
      { text: 'Delayed consult requests in 3 cases', type: 'bad', timeframe: '1 week' },
      { text: 'Incomplete discharge summaries', type: 'bad', timeframe: '1 week' },
      { text: 'Good medication dosing accuracy', type: 'good', timeframe: '1 month' },
      { text: 'Improving care coordination', type: 'good', timeframe: '2 months' },
    ],
  },
  4: { // Leehi
    'Patient Care': [
      { text: 'Exceptional patient advocacy', type: 'good', timeframe: '1 week' },
      { text: 'Thorough daily progress notes', type: 'good', timeframe: '1 week' },
      { text: 'Excellent symptom management', type: 'good', timeframe: '1 month' },
      { text: 'Outstanding end-of-life care discussions', type: 'good', timeframe: '2 months' },
    ],
    'History': [
      { text: 'Comprehensive social history documentation', type: 'good', timeframe: '1 week' },
      { text: 'Always asks about substance abuse', type: 'good', timeframe: '1 week' },
      { text: 'Thorough medication allergy documentation', type: 'good', timeframe: '1 month' },
      { text: 'Documents HbA1c in all diabetic patients', type: 'good', timeframe: '2 months' },
    ],
    'Medical Knowledge': [
      { text: 'Excellent understanding of complex cases', type: 'good', timeframe: '1 week' },
      { text: 'Strong pharmacology knowledge', type: 'good', timeframe: '1 month' },
      { text: 'Up-to-date with current literature', type: 'good', timeframe: '2 months' },
    ],
    'Physical Examination': [
      { text: 'Thorough systematic examinations', type: 'good', timeframe: '1 week' },
      { text: 'Excellent documentation of findings', type: 'good', timeframe: '1 month' },
      { text: 'Good teaching of examination techniques to students', type: 'good', timeframe: '2 months' },
    ],
    'Professionalism': [
      { text: 'Exemplary professional behavior', type: 'good', timeframe: '1 week' },
      { text: 'Mentor to junior residents', type: 'good', timeframe: '1 month' },
      { text: 'Excellent time management', type: 'good', timeframe: '2 months' },
    ],
    'Clinical Reasoning': [
      { text: 'Outstanding diagnostic skills', type: 'good', timeframe: '1 week' },
      { text: 'Systematic approach to complex problems', type: 'good', timeframe: '1 month' },
      { text: 'Excellent use of Bayesian reasoning', type: 'good', timeframe: '2 months' },
    ],
    'Interpersonal skills': [
      { text: 'Excellent communication with all team members', type: 'good', timeframe: '1 week' },
      { text: 'Skilled in breaking bad news', type: 'good', timeframe: '1 month' },
      { text: 'Strong conflict resolution abilities', type: 'good', timeframe: '2 months' },
    ],
    'Patient Management': [
      { text: 'Excellent care coordination', type: 'good', timeframe: '1 week' },
      { text: 'Proactive discharge planning', type: 'good', timeframe: '1 month' },
      { text: 'Strong multidisciplinary team leadership', type: 'good', timeframe: '2 months' },
    ],
  },
  5: { // Sivan
    'Patient Care': [
      { text: 'Good rapport with pediatric patients', type: 'good', timeframe: '1 week' },
      { text: 'Delayed pain reassessment in 2 cases', type: 'bad', timeframe: '1 week' },
      { text: 'Inconsistent family updates', type: 'bad', timeframe: '1 month' },
      { text: 'Improving bedside manner', type: 'good', timeframe: '2 months' },
    ],
    'History': [
      { text: 'Did not document immunization status', type: 'bad', timeframe: '1 week' },
      { text: 'Incomplete developmental history in pediatric cases', type: 'bad', timeframe: '1 week' },
      { text: 'Good allergy documentation', type: 'good', timeframe: '1 month' },
      { text: 'Missed birth history in neonates', type: 'bad', timeframe: '2 months' },
    ],
    'Medical Knowledge': [
      { text: 'Strong pediatric infectious disease knowledge', type: 'good', timeframe: '1 week' },
      { text: 'Gaps in neonatal care protocols', type: 'bad', timeframe: '1 week' },
      { text: 'Needs improvement in fluid management', type: 'bad', timeframe: '1 month' },
      { text: 'Good understanding of common pediatric conditions', type: 'good', timeframe: '2 months' },
    ],
    'Physical Examination': [
      { text: 'Did not check fontanelle in infant', type: 'bad', timeframe: '1 week' },
      { text: 'Incomplete hip examination in newborn', type: 'bad', timeframe: '1 week' },
      { text: 'Good respiratory examination technique', type: 'good', timeframe: '1 month' },
      { text: 'Missed red reflex examination', type: 'bad', timeframe: '2 months' },
    ],
    'Professionalism': [
      { text: 'Good attitude and work ethic', type: 'good', timeframe: '1 week' },
      { text: 'Sometimes incomplete handoffs', type: 'bad', timeframe: '1 month' },
      { text: 'Responsive to supervision', type: 'good', timeframe: '2 months' },
    ],
    'Clinical Reasoning': [
      { text: 'Good approach to common presentations', type: 'good', timeframe: '1 week' },
      { text: 'Needs improvement in rare diagnoses', type: 'bad', timeframe: '1 month' },
      { text: 'Sometimes anchors on initial diagnosis', type: 'bad', timeframe: '2 months' },
    ],
    'Interpersonal skills': [
      { text: 'Good communication with families', type: 'good', timeframe: '1 week' },
      { text: 'Needs improvement in interdisciplinary communication', type: 'bad', timeframe: '1 month' },
      { text: 'Supportive of colleagues', type: 'good', timeframe: '2 months' },
    ],
    'Patient Management': [
      { text: 'Delayed specialist consultations', type: 'bad', timeframe: '1 week' },
      { text: 'Incomplete care plans in 3 cases', type: 'bad', timeframe: '1 week' },
      { text: 'Good medication safety practices', type: 'good', timeframe: '1 month' },
      { text: 'Improving discharge planning', type: 'good', timeframe: '2 months' },
    ],
  },
  6: { // Noa
    'Patient Care': [
      { text: 'Excellent patient-centered approach', type: 'good', timeframe: '1 week' },
      { text: 'Thorough symptom assessment', type: 'good', timeframe: '1 month' },
      { text: 'Good follow-up documentation', type: 'good', timeframe: '2 months' },
    ],
    'History': [
      { text: 'Comprehensive history taking', type: 'good', timeframe: '1 week' },
      { text: 'Always documents cardiac risk factors', type: 'good', timeframe: '1 week' },
      { text: 'Occasionally misses occupational history', type: 'bad', timeframe: '1 month' },
      { text: 'Good psychiatric history documentation', type: 'good', timeframe: '2 months' },
    ],
    'Medical Knowledge': [
      { text: 'Strong internal medicine foundation', type: 'good', timeframe: '1 week' },
      { text: 'Good understanding of evidence-based medicine', type: 'good', timeframe: '1 month' },
      { text: 'Some gaps in hematology/oncology', type: 'bad', timeframe: '2 months' },
    ],
    'Physical Examination': [
      { text: 'Thorough cardiovascular examinations', type: 'good', timeframe: '1 week' },
      { text: 'Good abdominal examination technique', type: 'good', timeframe: '1 month' },
      { text: 'Occasionally incomplete peripheral vascular exam', type: 'bad', timeframe: '2 months' },
    ],
    'Professionalism': [
      { text: 'Excellent professional demeanor', type: 'good', timeframe: '1 week' },
      { text: 'Reliable and dependable', type: 'good', timeframe: '1 month' },
      { text: 'Good documentation timeliness', type: 'good', timeframe: '2 months' },
    ],
    'Clinical Reasoning': [
      { text: 'Solid diagnostic approach', type: 'good', timeframe: '1 week' },
      { text: 'Good use of clinical guidelines', type: 'good', timeframe: '1 month' },
      { text: 'Could improve in atypical presentations', type: 'bad', timeframe: '2 months' },
    ],
    'Interpersonal skills': [
      { text: 'Excellent patient communication', type: 'good', timeframe: '1 week' },
      { text: 'Good team collaboration', type: 'good', timeframe: '1 month' },
      { text: 'Effective handoff communication', type: 'good', timeframe: '2 months' },
    ],
    'Patient Management': [
      { text: 'Good care coordination', type: 'good', timeframe: '1 week' },
      { text: 'Timely discharge planning', type: 'good', timeframe: '1 month' },
      { text: 'Occasionally delayed follow-up arrangements', type: 'bad', timeframe: '2 months' },
    ],
  },
};

// Summary data per resident per timeframe
const residentSummaryData: { [residentId: number]: { [timeframe: string]: {
  learningPoints: string[];
  goodPractice: string[];
  overallSummary: string;
  exposureRecommendation: string;
}}} = {
  1: { // Shani
    '1 week': {
      learningPoints: [
        'Check meningeal signs in all febrile pediatric patients',
        'Document echocardiogram results consistently',
        'Improve timeliness of discharge planning',
        'Complete neurological examinations thoroughly',
        'Enhance medication reconciliation practices',
      ],
      goodPractice: [
        'Consistent follow-up documentation',
        'Thorough allergy documentation',
        'Strong pediatric pharmacology knowledge',
        'Always punctual for rounds',
        'Good systematic approach to fever workup',
      ],
      overallSummary: 'This past month, Dr. Shani Erez demonstrated strengths in documentation and punctuality but had notable gaps in physical examination completeness, particularly meningeal signs and neurological assessments. Immediate focus should be on systematic examination checklists.',
      exposureRecommendation: 'This month: Focus on neurology and infectious disease cases to improve examination skills.',
    },
    '1 month': {
      learningPoints: [
        'Document meningeal signs assessment in all febrile pediatric patients',
        'Complete HbA1c documentation in diabetic patients',
        'Improve discharge planning timeliness and coordination',
        'Ensure thorough surgical history documentation',
        'Address gaps in neonatal resuscitation protocols',
      ],
      goodPractice: [
        'Excellent bedside manner with pediatric patients and families',
        'Strong pediatric pharmacology knowledge and application',
        'Consistent documentation of allergies and substance abuse history',
        'Excellent communication with nursing staff',
        'Good antibiotic stewardship practices',
      ],
      overallSummary: 'Over the past 3 months, Dr. Shani Erez has shown consistent interpersonal strengths and good documentation habits. However, a pattern of incomplete physical examinations and delayed discharge planning requires focused attention. Consider structured examination checklists.',
      exposureRecommendation: 'Past 3 months: Recommend increased exposure to Nephrology and Gastroenterology cases.',
    },
    '2 months': {
      learningPoints: [
        'Document meningeal signs assessment in all febrile pediatric patients',
        'Complete HbA1c documentation in diabetic patients',
        'Improve discharge planning timeliness and coordination',
        'Ensure thorough surgical history documentation',
        'Review travel history protocols for infectious cases',
      ],
      goodPractice: [
        'Excellent bedside manner and rapport with pediatric patients and families',
        'Strong pediatric pharmacology knowledge and application',
        'Consistent documentation of allergies and substance abuse history',
        'Proactive approach to seeking feedback and self-improvement',
        'Improved pattern recognition skills over time',
      ],
      overallSummary: 'Over 6 months, Dr. Shani Erez shows steady improvement in clinical reasoning and pattern recognition. Persistent gaps in systematic physical examination and discharge coordination remain areas for focused development. The proactive approach to feedback is a strong foundation for continued growth.',
      exposureRecommendation: 'Past 6 months: Continue Nephrology and Gastroenterology exposure; add complex infectious disease cases.',
    },
    '6 months': {
      learningPoints: [
        'Document meningeal signs assessment in all febrile pediatric patients',
        'Complete HbA1c documentation in diabetic patients',
        'Improve discharge planning timeliness and coordination',
        'Ensure thorough surgical history documentation',
        'Enhance knowledge of rare metabolic disorders',
      ],
      goodPractice: [
        'Excellent bedside manner and rapport with pediatric patients and families',
        'Strong pediatric pharmacology knowledge and application',
        'Consistent documentation of allergies and substance abuse history',
        'Proactive approach to seeking feedback and self-improvement',
        'Good antibiotic stewardship and evidence-based prescribing',
      ],
      overallSummary: 'Over the past year, Dr. Shani Erez has demonstrated significant growth in clinical skills and maintains strong interpersonal abilities. Systematic physical examination documentation remains the primary area for improvement. Ready for additional responsibilities with continued mentorship.',
      exposureRecommendation: 'Annual review: Nephrology, Gastroenterology, and rare metabolic disorder cases recommended.',
    },
  },
  2: { // Hillel
    '1 week': {
      learningPoints: [
        'Continue exemplary clinical practices',
        'Document teaching sessions for portfolio',
        'Consider leading quality improvement initiative',
        'Share examination techniques with junior residents',
        'Maintain current high standards',
      ],
      goodPractice: [
        'Thorough daily assessments documented',
        'Excellent pain management protocols',
        'Comprehensive medication reconciliation',
        'Strong cardiology knowledge base',
        'Excellent diagnostic approach in complex cases',
      ],
      overallSummary: 'This past month, Dr. Hillel Sztern performed exceptionally across all domains. Focus should be on leadership development and sharing expertise with peers. Consider formal teaching role.',
      exposureRecommendation: 'This month: Consider advanced cardiology or critical care rotations based on interest.',
    },
    '1 month': {
      learningPoints: [
        'Document previous asthma exacerbations consistently',
        'Continue excellence in clinical care',
        'Develop formal teaching curriculum',
        'Lead department educational sessions',
        'Mentor struggling residents',
      ],
      goodPractice: [
        'Excellent pain management and patient advocacy',
        'Always documents smoking status',
        'Up-to-date on latest clinical guidelines',
        'Role model for junior residents',
        'Strong evidence-based decision making',
      ],
      overallSummary: 'Over 3 months, Dr. Hillel Sztern maintains outstanding performance with minor documentation gaps in respiratory history. Ready for chief resident consideration and formal leadership roles.',
      exposureRecommendation: 'Past 3 months: Expand rheumatology exposure; otherwise well-rounded experience.',
    },
    '2 months': {
      learningPoints: [
        'Ensure consistent documentation of previous asthma exacerbations',
        'Improve punctuality for morning handoffs',
        'Expand rheumatology knowledge base',
        'Continue developing rare disease recognition',
        'Maintain high standards while mentoring juniors',
      ],
      goodPractice: [
        'Excellent cardiovascular examination and diagnostic skills',
        'Strong evidence-based clinical reasoning approach',
        'Comprehensive medication reconciliation and documentation',
        'Outstanding team leadership and collaboration',
        'Proactive discharge planning and care coordination',
      ],
      overallSummary: 'Over 6 months, Dr. Hillel Sztern demonstrates excellent clinical skills across most domains and serves as a role model for junior residents. Minor improvements needed in punctuality and subspecialty knowledge. Ready for leadership responsibilities.',
      exposureRecommendation: 'Past 6 months: Additional rheumatology and rare disease cases to round out experience.',
    },
    '6 months': {
      learningPoints: [
        'Ensure consistent documentation of previous asthma exacerbations',
        'Improve punctuality for morning handoffs',
        'Expand rheumatology knowledge base',
        'Continue developing rare disease recognition',
        'Maintain high standards while mentoring juniors',
      ],
      goodPractice: [
        'Excellent cardiovascular examination and diagnostic skills',
        'Strong evidence-based clinical reasoning approach',
        'Comprehensive medication reconciliation and documentation',
        'Outstanding team leadership and collaboration',
        'Proactive discharge planning and care coordination',
      ],
      overallSummary: 'Annual review: Dr. Hillel Sztern is performing at an exceptional level across all measured domains. Recommend consideration for chief resident position or teaching roles. Focus should be on leadership development and sharing expertise with peers.',
      exposureRecommendation: 'Annual: Consider advanced subspecialty rotations based on career interest. No urgent gaps.',
    },
  },
  3: { // Maayan
    '1 week': {
      learningPoints: [
        'Document echocardiogram results in all cardiac patients',
        'Complete surgical history documentation',
        'Ask about smoking history in all patients',
        'Improve response time to nursing calls',
        'Develop systematic approach to differential diagnosis',
      ],
      goodPractice: [
        'Good documentation of patient preferences',
        'Good attitude toward learning',
        'Good patient rapport',
        'Good medication dosing accuracy',
        'Receptive to feedback',
      ],
      overallSummary: 'This past month, Dr. Maayan showed willingness to learn but had multiple documentation gaps and delayed clinical responses. Immediate focus needed on systematic history taking and timely clinical assessments.',
      exposureRecommendation: 'This month: Urgent focus on cardiology cases with structured examination teaching.',
    },
    '1 month': {
      learningPoints: [
        'Develop systematic approach to history taking - use checklists',
        'Improve clinical reasoning structure for differential diagnosis',
        'Complete physical examinations including JVP and fundoscopy',
        'Address gaps in acute coronary syndrome protocols',
        'Complete discharge summaries before patient leaves',
      ],
      goodPractice: [
        'Good patient rapport and family history documentation',
        'Appropriate antibiotic selection practices',
        'Positive attitude toward learning and receptive to feedback',
        'Good collaboration with team members',
        'Good musculoskeletal examination technique',
      ],
      overallSummary: 'Over 3 months, Dr. Maayan is in an early learning phase with significant room for growth. The positive attitude and receptiveness to feedback provide a good foundation. Focus should be on developing systematic approaches to history, examination, and clinical reasoning.',
      exposureRecommendation: 'Past 3 months: Urgently needs more cardiology exposure and structured teaching on systematic examination.',
    },
    '2 months': {
      learningPoints: [
        'Develop systematic approach to history taking - use checklists',
        'Improve clinical reasoning structure for differential diagnosis',
        'Complete physical examinations including JVP, fundoscopy, and peripheral edema',
        'Enhance timeliness of consult requests',
        'Include occupational exposure in history',
      ],
      goodPractice: [
        'Good patient rapport and family history documentation',
        'Appropriate antibiotic selection practices',
        'Positive attitude toward learning and receptive to feedback',
        'Good collaboration with team members',
        'Improving pattern recognition over time',
      ],
      overallSummary: 'Over 6 months, Dr. Maayan shows gradual improvement in some areas but persistent gaps in systematic examination and history. The learning curve requires continued intensive supervision and structured educational interventions.',
      exposureRecommendation: 'Past 6 months: Intensive cardiology and internal medicine core rotations with direct supervision.',
    },
    '6 months': {
      learningPoints: [
        'Develop systematic approach to history taking - use checklists',
        'Improve clinical reasoning structure for differential diagnosis',
        'Complete physical examinations including JVP, fundoscopy, and peripheral edema',
        'Enhance timeliness of consult requests',
        'Complete discharge summaries before patient leaves',
      ],
      goodPractice: [
        'Good patient rapport and family history documentation',
        'Appropriate antibiotic selection practices',
        'Positive attitude toward learning and receptive to feedback',
        'Good collaboration with team members',
        'Accurate medication dosing calculations',
      ],
      overallSummary: 'Annual review: Dr. Maayan requires continued focused development with structured supervision. While showing improvement in attitude and collaboration, systematic clinical skills remain below expected level. Recommend remediation plan with clear milestones.',
      exposureRecommendation: 'Annual: Structured rotation through all core internal medicine subspecialties with competency assessments.',
    },
  },
  4: { // Leehi
    '1 week': {
      learningPoints: [
        'Continue excellence - consider teaching role',
        'Document innovative approaches for protocols',
        'Share best practices with peers',
        'Lead educational sessions',
        'Mentor residents needing support',
      ],
      goodPractice: [
        'Exceptional patient advocacy',
        'Thorough daily progress notes',
        'Comprehensive social history documentation',
        'Excellent understanding of complex cases',
        'Outstanding diagnostic skills',
      ],
      overallSummary: 'This past month, Dr. Leehi Pridan performed at an exceptional level. All domains show excellence. Focus should be on leadership development and sharing expertise.',
      exposureRecommendation: 'This month: Consider advanced subspecialty exposure based on career goals.',
    },
    '1 month': {
      learningPoints: [
        'Continue excellence - consider teaching role for peers',
        'Document innovative approaches for department protocols',
        'Share best practices in structured format',
        'Develop quality improvement project leadership',
        'Mentor residents who need additional support',
      ],
      goodPractice: [
        'Exemplary systematic approach across all clinical domains',
        'Outstanding patient advocacy and communication skills',
        'Comprehensive documentation setting department standard',
        'Excellent symptom management and end-of-life discussions',
        'Strong pharmacology knowledge',
      ],
      overallSummary: 'Over 3 months, Dr. Leehi Pridan maintains outstanding performance across all domains. Ready for chief resident consideration and formal leadership roles.',
      exposureRecommendation: 'Past 3 months: No gaps identified. Consider leadership in complex case management.',
    },
    '2 months': {
      learningPoints: [
        'Continue excellence - consider teaching role for peers',
        'Document innovative approaches for department protocols',
        'Share best practices in structured format',
        'Develop quality improvement project leadership',
        'Mentor residents who need additional support',
      ],
      goodPractice: [
        'Exemplary systematic approach across all clinical domains',
        'Outstanding patient advocacy and communication skills',
        'Comprehensive documentation setting department standard',
        'Excellent clinical reasoning with Bayesian approach',
        'Strong multidisciplinary team leadership',
      ],
      overallSummary: 'Over 6 months, Dr. Leehi Pridan consistently performs at an exceptional level. Recommend consideration for chief resident position or teaching roles. Focus should be on leadership development and sharing expertise with peers.',
      exposureRecommendation: 'Past 6 months: Consider advanced subspecialty rotations based on career interest. No urgent exposure gaps.',
    },
    '6 months': {
      learningPoints: [
        'Continue excellence - consider teaching role for peers',
        'Document innovative approaches for department protocols',
        'Share best practices in structured format',
        'Develop quality improvement project leadership',
        'Mentor residents who need additional support',
      ],
      goodPractice: [
        'Exemplary systematic approach across all clinical domains',
        'Outstanding patient advocacy and communication skills',
        'Comprehensive documentation setting department standard',
        'Excellent clinical reasoning with Bayesian approach',
        'Strong multidisciplinary team leadership',
      ],
      overallSummary: 'Annual review: Dr. Leehi Pridan is performing at an exceptional level across all measured domains. Highly recommend for chief resident position or teaching roles. Focus should be on leadership development and sharing expertise with peers.',
      exposureRecommendation: 'Annual: Consider advanced subspecialty rotations based on career interest. No urgent exposure gaps identified.',
    },
  },
  5: { // Sivan
    '1 week': {
      learningPoints: [
        'Document immunization status in all pediatric patients',
        'Complete developmental history documentation',
        'Check fontanelle in all infants',
        'Complete hip examination in newborns',
        'Improve timeliness of specialist consultations',
      ],
      goodPractice: [
        'Good rapport with pediatric patients',
        'Strong pediatric infectious disease knowledge',
        'Good attitude and work ethic',
        'Good approach to common presentations',
        'Good communication with families',
      ],
      overallSummary: 'This past month, Dr. Sivan Kijner showed some strengths in infectious disease knowledge but had significant gaps in neonatal examination and documentation. Immediate focus needed on systematic neonatal assessment.',
      exposureRecommendation: 'This month: Priority NICU and well-baby nursery exposure with direct supervision.',
    },
    '1 month': {
      learningPoints: [
        'Document immunization and developmental history in all pediatric cases',
        'Complete neonatal examination including fontanelle and hip assessment',
        'Improve fluid management knowledge',
        'Address gaps in neonatal care protocols',
        'Ensure complete handoffs at transitions',
      ],
      goodPractice: [
        'Good rapport with pediatric patients',
        'Strong pediatric infectious disease knowledge',
        'Good allergy documentation',
        'Good respiratory examination technique',
        'Good medication safety practices',
      ],
      overallSummary: 'Over 3 months, Dr. Sivan Kijner shows potential but has significant gaps in neonatal care and systematic examination that require focused attention. The stagnant score trend suggests need for more intensive supervision.',
      exposureRecommendation: 'Past 3 months: Priority NICU and well-baby nursery. Also recommend neurology experience.',
    },
    '2 months': {
      learningPoints: [
        'Document immunization and developmental history in all pediatric cases',
        'Complete neonatal examination including fontanelle and hip assessment',
        'Improve timeliness of specialist consultations',
        'Enhance consistency of family communication',
        'Develop structured approach to care planning',
      ],
      goodPractice: [
        'Strong pediatric infectious disease knowledge',
        'Good respiratory examination technique',
        'Positive attitude and work ethic',
        'Responsive to supervision and feedback',
        'Good medication safety awareness',
      ],
      overallSummary: 'Over 6 months, Dr. Sivan Kijner shows potential but has significant gaps in neonatal care and systematic examination that require focused attention. The stagnant score trend suggests need for more intensive supervision and structured learning objectives.',
      exposureRecommendation: 'Past 6 months: Priority NICU and well-baby nursery cases. Also recommend additional Neurology experience.',
    },
    '6 months': {
      learningPoints: [
        'Document immunization and developmental history in all pediatric cases',
        'Complete neonatal examination including fontanelle and hip assessment',
        'Improve timeliness of specialist consultations',
        'Enhance consistency of family communication',
        'Develop structured approach to care planning',
      ],
      goodPractice: [
        'Strong pediatric infectious disease knowledge',
        'Good respiratory examination technique',
        'Positive attitude and work ethic',
        'Responsive to supervision and feedback',
        'Good medication safety awareness',
      ],
      overallSummary: 'Annual review: Dr. Sivan Kijner requires focused development in neonatal care and systematic examination. The lack of score improvement over the year indicates need for structured remediation plan with clear milestones and increased supervision.',
      exposureRecommendation: 'Annual: Structured NICU rotation with competency milestones. Additional neurology and general pediatrics core experience.',
    },
  },
  6: { // Noa
    '1 week': {
      learningPoints: [
        'Continue strong clinical practices',
        'Include occupational history routinely',
        'Expand hematology/oncology knowledge',
        'Consider research or QI project',
        'Mentor junior colleagues',
      ],
      goodPractice: [
        'Excellent patient-centered approach',
        'Comprehensive history taking',
        'Always documents cardiac risk factors',
        'Strong internal medicine foundation',
        'Solid diagnostic approach',
      ],
      overallSummary: 'This past month, Dr. Noa Shohat demonstrated solid clinical competence with consistent performance. Minor areas for improvement in occupational history. Well-positioned for increased responsibilities.',
      exposureRecommendation: 'This month: Consider hematology/oncology elective to address knowledge gaps.',
    },
    '1 month': {
      learningPoints: [
        'Include occupational and environmental history routinely',
        'Expand hematology/oncology knowledge base',
        'Improve recognition of atypical presentations',
        'Consider teaching or research role',
        'Continue professional development',
      ],
      goodPractice: [
        'Excellent patient-centered care approach',
        'Comprehensive cardiac risk factor documentation',
        'Strong internal medicine foundation',
        'Reliable and professional demeanor',
        'Good use of clinical guidelines',
      ],
      overallSummary: 'Over 3 months, Dr. Noa Shohat demonstrates solid clinical competence with consistent performance across domains. Minor areas for improvement identified. Well-positioned for increased clinical responsibilities.',
      exposureRecommendation: 'Past 3 months: Recommend hematology and oncology exposure to address knowledge gaps.',
    },
    '2 months': {
      learningPoints: [
        'Include occupational and environmental history routinely',
        'Complete peripheral vascular examination in relevant cases',
        'Expand hematology/oncology knowledge base',
        'Improve recognition of atypical disease presentations',
        'Ensure timely follow-up arrangements before discharge',
      ],
      goodPractice: [
        'Excellent patient-centered care approach',
        'Comprehensive cardiac risk factor documentation',
        'Strong internal medicine foundation',
        'Reliable and professional demeanor',
        'Effective team and patient communication',
      ],
      overallSummary: 'Over 6 months, Dr. Noa Shohat demonstrates solid clinical competence with consistent performance across domains. Minor areas for improvement identified. Well-positioned for increased clinical responsibilities.',
      exposureRecommendation: 'Past 6 months: Recommend additional exposure to Hematology and Oncology cases.',
    },
    '6 months': {
      learningPoints: [
        'Include occupational and environmental history routinely',
        'Complete peripheral vascular examination in relevant cases',
        'Expand hematology/oncology knowledge base',
        'Improve recognition of atypical disease presentations',
        'Ensure timely follow-up arrangements before discharge',
      ],
      goodPractice: [
        'Excellent patient-centered care approach',
        'Comprehensive cardiac risk factor documentation',
        'Strong internal medicine foundation',
        'Reliable and professional demeanor',
        'Effective team and patient communication',
      ],
      overallSummary: 'Annual review: Dr. Noa Shohat demonstrates solid clinical competence with consistent performance across domains. Minor areas for improvement identified in subspecialty knowledge. Ready for increased clinical responsibilities and potential teaching role.',
      exposureRecommendation: 'Annual: Recommend additional exposure to Hematology and Oncology cases to address knowledge gaps.',
    },
  },
};

// Mock data for different skills
const mockTrendData: { [key: string]: { mean: number[]; syllabus: number[]; actual: number[] } } = {
  'Patient Care': {
    mean: [65, 68, 72, 75, 78, 80],
    syllabus: [70, 72, 74, 76, 78, 80],
    actual: [60, 70, 75, 72, 85, 79],
  },
  'History': {
    mean: [62, 65, 68, 70, 72, 74],
    syllabus: [65, 68, 70, 72, 75, 78],
    actual: [58, 65, 72, 68, 78, 82],
  },
  'Medical Knowledge': {
    mean: [70, 72, 74, 76, 78, 80],
    syllabus: [72, 74, 76, 78, 80, 82],
    actual: [68, 75, 80, 78, 85, 88],
  },
  'Physical Examination': {
    mean: [55, 58, 60, 62, 65, 68],
    syllabus: [60, 62, 65, 68, 70, 72],
    actual: [50, 55, 58, 60, 55, 56],
  },
  'Professionalism': {
    mean: [80, 82, 84, 85, 87, 88],
    syllabus: [82, 84, 86, 88, 90, 92],
    actual: [85, 88, 90, 92, 90, 91],
  },
  'Clinical Reasoning': {
    mean: [60, 63, 66, 68, 70, 72],
    syllabus: [65, 68, 70, 72, 75, 78],
    actual: [55, 62, 68, 65, 72, 75],
  },
  'Interpersonal skills': {
    mean: [75, 78, 80, 82, 84, 85],
    syllabus: [78, 80, 82, 84, 86, 88],
    actual: [72, 78, 82, 85, 88, 86],
  },
  'Patient Management': {
    mean: [50, 52, 55, 58, 60, 62],
    syllabus: [55, 58, 60, 62, 65, 68],
    actual: [45, 48, 38, 35, 38, 36],
  },
};

// Left column skills
const leftColumnSkills = [
  'Patient Care',
  'Medical Knowledge',
  'Professionalism',
  'Interpersonal skills',
];

// Right column skills
const rightColumnSkills = [
  'History',
  'Physical Examination',
  'Clinical Reasoning',
  'Patient Management',
];

// Claude API key for generating medical records
const CLAUDE_API_KEY = 'sk-ant-api03-YOUR_KEY_HERE'; // Replace with actual key

const TrendsSection: React.FC<TrendsSectionProps> = ({ doctorName, residentId = 1, globalTimeframe }) => {
  const [selectedSkill, setSelectedSkill] = useState('Patient Care');
  // Use global timeframe if provided, otherwise use local state
  const timeFrame = globalTimeframe || '3 month';
  const [selectedInsight, setSelectedInsight] = useState<Insight | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [generatedRecord, setGeneratedRecord] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);

  const labels = ['Week 1', 'Week 4', 'Week 8', 'Week 10', 'Week 12', 'Now'];

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Mean Score',
        data: mockTrendData[selectedSkill]?.mean || [],
        borderColor: '#D8B4FE',
        backgroundColor: 'rgba(216, 180, 254, 0.1)',
        tension: 0.4,
        borderWidth: 3,
        pointRadius: 0,
      },
      {
        label: 'Syllabus',
        data: mockTrendData[selectedSkill]?.syllabus || [],
        borderColor: '#A855F7',
        backgroundColor: 'rgba(168, 85, 247, 0.1)',
        tension: 0.4,
        borderWidth: 3,
        pointRadius: 0,
      },
      {
        label: 'Actual Score',
        data: mockTrendData[selectedSkill]?.actual || [],
        borderColor: '#581C87',
        backgroundColor: 'rgba(88, 28, 135, 0.1)',
        tension: 0.4,
        borderWidth: 3,
        pointRadius: 6,
        pointBackgroundColor: '#581C87',
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        align: 'end' as const,
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 20,
          font: {
            size: 12,
          },
        },
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        min: 0,
        max: 100,
        grid: {
          color: '#F3F4F6',
        },
        ticks: {
          display: true,
          stepSize: 20,
          font: {
            size: 11,
          },
          color: '#6B7280',
          callback: function(value: number | string) {
            return value + '%';
          },
        },
        border: {
          display: false,
        },
        title: {
          display: true,
          text: 'Score (%)',
          font: {
            size: 12,
            weight: 'bold' as const,
          },
          color: '#4B5563',
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          display: true,
          font: {
            size: 11,
          },
          color: '#6B7280',
        },
        border: {
          display: false,
        },
        title: {
          display: true,
          text: 'Time',
          font: {
            size: 12,
            weight: 'bold' as const,
          },
          color: '#4B5563',
        },
      },
    },
  };

  // Get insights for current resident and skill, filtered by timeframe
  const allInsights = residentInsightsData[residentId]?.[selectedSkill] || [];
  const currentInsights = filterInsightsByTimeframe(allInsights, timeFrame);
  
  // Get summary for current timeframe
  const summary = residentSummaryData[residentId]?.[timeFrame];

  // Generate medical record using Claude API
  const generateMedicalRecord = async (insight: Insight) => {
    setIsGenerating(true);
    setGeneratedRecord('');
    
    const insightType = insight.type === 'good' ? 'positive practice' : 'area for improvement';
    const prompt = `Generate a realistic, detailed medical admission note in the style of hospital discharge summaries. This note should clearly demonstrate the following ${insightType} by the resident physician: "${insight.text}"

The admission note should be for a patient case that would naturally reveal this ${insightType}. Include:
- Admission and discharge dates (use dates like [**2124-X-XX**])
- Service
- Allergies
- Attending physician (use format [**First Name3 (LF) XXX**])
- Chief Complaint
- Major procedures if relevant
- History of Present Illness (detailed)
- Past Medical History
- Social History
- Family History
- Physical Exam on admission
- Pertinent Results (labs, imaging)
- Brief Hospital Course (this is where the insight should be most evident)
- Medications on Admission
- Discharge Medications
- Discharge Disposition
- Discharge Diagnosis
- Discharge Condition
- Discharge Instructions
- Follow-up Instructions

Make the ${insightType === 'positive practice' ? 'good documentation/care' : 'gap or missing element'} clearly visible in the appropriate sections. The note should be medically accurate and realistic.

Format the output as a plain text medical document similar to hospital discharge summaries.`;

    try {
      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': CLAUDE_API_KEY,
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true',
        },
        body: JSON.stringify({
          model: 'claude-3-haiku-20240307',
          max_tokens: 4000,
          messages: [
            {
              role: 'user',
              content: prompt,
            },
          ],
        }),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      const generatedText = data.content[0]?.text || 'Failed to generate record';
      setGeneratedRecord(generatedText);
    } catch (error) {
      console.error('Error generating medical record:', error);
      // Fallback to mock data if API fails
      setGeneratedRecord(generateMockRecord(insight, doctorName));
    } finally {
      setIsGenerating(false);
    }
  };

  // Mock record generator as fallback
  const generateMockRecord = (insight: Insight, residentName: string): string => {
    const isGood = insight.type === 'good';
    const randomDate = Math.floor(Math.random() * 28) + 1;
    const randomMonth = Math.floor(Math.random() * 12) + 1;
    const insightLower = insight.text.toLowerCase();
    
    // Determine which section the evidence belongs in based on insight content
    type EvidenceSection = 'allergies' | 'social_history' | 'physical_exam_neck' | 'physical_exam_neuro' | 'pertinent_results' | 'hospital_course' | 'discharge_instructions';
    
    const getEvidenceSection = (): EvidenceSection => {
      if (insightLower.includes('allergy') || insightLower.includes('allergies')) return 'allergies';
      if (insightLower.includes('substance') || insightLower.includes('smoking') || insightLower.includes('alcohol') || insightLower.includes('tobacco')) return 'social_history';
      if (insightLower.includes('meningeal') || insightLower.includes('nuchal') || insightLower.includes('neck')) return 'physical_exam_neck';
      if (insightLower.includes('neuro') || insightLower.includes('neurological') || insightLower.includes('cranial')) return 'physical_exam_neuro';
      if (insightLower.includes('hba1c') || insightLower.includes('echo') || insightLower.includes('echocardiogram') || insightLower.includes('lab') || insightLower.includes('result')) return 'pertinent_results';
      if (insightLower.includes('discharge') && (insightLower.includes('instruction') || insightLower.includes('education'))) return 'discharge_instructions';
      // Default to hospital course for behavioral/process insights
      return 'hospital_course';
    };
    
    const evidenceSection = getEvidenceSection();
    
    // Generate the highlighted evidence text based on insight
    const generateEvidence = (): string => {
      // Allergy-related
      if (evidenceSection === 'allergies') {
        return isGood ?
          '[[HIGHLIGHT]]Allergies thoroughly documented with reaction types:\n- Penicillin (anaphylaxis - documented [**2115**])\n- Sulfa drugs (rash)\n- Contrast dye (premedication protocol noted)[[/HIGHLIGHT]]' :
          '[[HIGHLIGHT]][DOCUMENTATION GAP: Allergy list incomplete - reaction types and severity not specified for documented allergies][[/HIGHLIGHT]]';
      }
      
      // Social history related
      if (evidenceSection === 'social_history') {
        return isGood ?
          '[[HIGHLIGHT]]Substance use thoroughly documented:\n- Alcohol: Social use, 2-3 drinks per week\n- Tobacco: Quit 10 years ago, 20 pack-year history\n- Illicit drugs: Denies current or past use\nLives with spouse. Retired teacher. Independent with ADLs.[[/HIGHLIGHT]]' :
          '[[HIGHLIGHT]][DOCUMENTATION GAP: Substance use history not documented - critical social history missing. Smoking status, alcohol use, and drug use not addressed.][[/HIGHLIGHT]]';
      }
      
      // Meningeal signs
      if (evidenceSection === 'physical_exam_neck') {
        return isGood ?
          '[[HIGHLIGHT]]Neck: Supple, full ROM. Meningeal signs assessed:\n- Kernig sign: Negative bilaterally\n- Brudzinski sign: Negative\n- Nuchal rigidity: Absent\nNo lymphadenopathy.[[/HIGHLIGHT]]' :
          '[[HIGHLIGHT]][CRITICAL OMISSION: Meningeal signs not assessed in febrile patient with headache. Kernig, Brudzinski, and nuchal rigidity examination not documented - essential for ruling out meningitis.][[/HIGHLIGHT]]';
      }
      
      // Neurological exam
      if (evidenceSection === 'physical_exam_neuro') {
        return isGood ?
          '[[HIGHLIGHT]]Neuro: Complete examination performed:\n- Mental status: A&O x3, following commands\n- Cranial nerves: II-XII intact\n- Motor: 5/5 strength all extremities\n- Sensory: Intact to light touch throughout\n- Reflexes: 2+ and symmetric\n- Coordination: Finger-to-nose intact\n- Gait: Normal[[/HIGHLIGHT]]' :
          '[[HIGHLIGHT]][INCOMPLETE EXAMINATION: Neurological exam documented as "grossly intact" without details. Cranial nerve examination not performed. Reflexes not assessed. Gait not evaluated.][[/HIGHLIGHT]]';
      }
      
      // Lab results
      if (evidenceSection === 'pertinent_results') {
        if (insightLower.includes('hba1c')) {
          return isGood ?
            `[[HIGHLIGHT]]HbA1c: 7.8% (obtained [**2124-${randomMonth}-${randomDate}**])\nInterpretation: Indicates suboptimal glycemic control. Discussed with patient, adjustments to diabetes management recommended.[[/HIGHLIGHT]]` :
            '[[HIGHLIGHT]][MISSING DATA: HbA1c not obtained despite diabetic patient with glucose 156 on admission. Glycemic control assessment incomplete - cannot evaluate long-term diabetes management.][[/HIGHLIGHT]]';
        }
        if (insightLower.includes('echo') || insightLower.includes('echocardiogram')) {
          return isGood ?
            '[[HIGHLIGHT]]Previous Echo ([**2124-3-15**]) reviewed and documented:\n- EF: 55% (normal)\n- Wall motion: No regional abnormalities\n- Valves: Mild MR, no AS/AR\n- No pericardial effusion\nResults incorporated into clinical decision-making.[[/HIGHLIGHT]]' :
            '[[HIGHLIGHT]][DOCUMENTATION GAP: Previous echocardiogram results not reviewed or documented. Patient with cardiac history - prior echo from [**2124-3-15**] not referenced in chart. Important for comparison.][[/HIGHLIGHT]]';
        }
        return isGood ?
          '[[HIGHLIGHT]]All pertinent results reviewed and documented with clinical interpretation. Abnormal values addressed in assessment and plan.[[/HIGHLIGHT]]' :
          '[[HIGHLIGHT]][RESULTS NOT DOCUMENTED: Pertinent laboratory/imaging results not reviewed or incorporated into clinical assessment.][[/HIGHLIGHT]]';
      }
      
      // Discharge instructions
      if (evidenceSection === 'discharge_instructions') {
        return isGood ?
          '[[HIGHLIGHT]]Comprehensive discharge education provided:\n- Medication changes reviewed with patient and family\n- Return precautions clearly explained (fever >101°F, worsening symptoms, new symptoms)\n- Activity restrictions discussed\n- Follow-up appointments confirmed\n- Patient verbalized understanding, questions answered[[/HIGHLIGHT]]' :
          '[[HIGHLIGHT]][PROCESS ISSUE: Discharge planning delayed - social work not consulted until day 4, resulting in 2-day discharge delay. Follow-up appointments not arranged until day of discharge. Patient education documentation incomplete.][[/HIGHLIGHT]]';
      }
      
      // Hospital course - default section for behavioral/process insights
      // Match specific insight types
      if (insightLower.includes('bedside') || insightLower.includes('rapport') || insightLower.includes('manner')) {
        return '[[HIGHLIGHT]]RESIDENT PERFORMANCE NOTE: Resident demonstrated excellent rapport with patient and family throughout hospitalization. Patient specifically commented: "Dr. took time to explain everything clearly." Family expressed appreciation for compassionate care and thorough communication. Resident took extra time to address all concerns and ensured understanding of treatment plan.[[/HIGHLIGHT]]';
      }
      if (insightLower.includes('pain')) {
        return isGood ?
          '[[HIGHLIGHT]]Pain Management:\nDay 1: Pain 8/10 on admission, IV morphine initiated\nDay 2: Pain reassessed q4h, 4/10 with current regimen\nDay 3: Transitioned to PO, pain well controlled 2-3/10\nPatient comfort maintained throughout with appropriate titration.[[/HIGHLIGHT]]' :
          '[[HIGHLIGHT]][QUALITY CONCERN: Pain Management Gap Identified]\nPost-operative note documents pain assessment at 08:00.\nNext documented assessment at 16:30 - 8.5 hour gap.\nPatient reported uncontrolled pain (8/10) for extended period.\nNursing notes indicate multiple requests for pain medication during this interval.[[/HIGHLIGHT]]';
      }
      if (insightLower.includes('family') || insightLower.includes('communication')) {
        return isGood ?
          '[[HIGHLIGHT]]Family Communication Log:\nDay 1: Family meeting held, diagnosis and plan discussed\nDay 2: Spouse updated on progress, questions addressed\nDay 3: Care conference with daughter via phone\nDay 4: Discharge planning discussed with patient and wife\nAll communications documented with family member names and content discussed.[[/HIGHLIGHT]]' :
          '[[HIGHLIGHT]][DOCUMENTATION GAP: Family Communication]\nDays 2-4: No family communication documented despite:\n- Complex medical course requiring decision-making\n- Multiple medication changes\n- Change in code status discussion needed\nFamily later reported feeling "out of the loop" regarding patient care.[[/HIGHLIGHT]]';
      }
      if (insightLower.includes('follow-up') || insightLower.includes('documentation')) {
        return isGood ?
          '[[HIGHLIGHT]]Documentation Quality:\n- Daily progress notes comprehensive with clear A&P\n- All orders include clinical reasoning\n- Handoff documentation complete\n- Consultant recommendations addressed and documented\n- Medication reconciliation performed and documented daily[[/HIGHLIGHT]]' :
          '[[HIGHLIGHT]][DOCUMENTATION ISSUE: Progress notes for days 2-3 lack specific assessment and plan. Orders placed without documented reasoning. Handoff note incomplete - pending items not listed.][[/HIGHLIGHT]]';
      }
      if (insightLower.includes('sepsis') || insightLower.includes('deteriorat')) {
        return isGood ?
          '[[HIGHLIGHT]]Early Recognition: Patient developed tachycardia and hypotension on Day 2 AM. Resident immediately recognized signs of clinical deterioration, initiated sepsis workup, obtained cultures, and started empiric antibiotics within 45 minutes. Rapid response appropriately activated. Excellent clinical judgment demonstrated.[[/HIGHLIGHT]]' :
          '[[HIGHLIGHT]][DELAYED RECOGNITION: Vital sign changes (HR 112, BP 88/54) documented at 06:00 but not addressed until 11:00 when patient became more symptomatic. 5-hour delay in sepsis recognition and treatment initiation.][[/HIGHLIGHT]]';
      }
      if (insightLower.includes('medication') || insightLower.includes('reconcil')) {
        return isGood ?
          '[[HIGHLIGHT]]Medication Safety: Complete medication reconciliation performed on admission. Home medications verified with pharmacy. Drug interactions checked - metformin held appropriately for contrast procedure. Patient education on all medication changes documented.[[/HIGHLIGHT]]' :
          '[[HIGHLIGHT]][MEDICATION SAFETY CONCERN: Home medication list incomplete on admission. Metformin continued despite contrast procedure scheduled. Medication reconciliation not documented until day of discharge.][[/HIGHLIGHT]]';
      }
      if (insightLower.includes('consult') || insightLower.includes('coordination')) {
        return isGood ?
          '[[HIGHLIGHT]]Care Coordination: Cardiology consulted day 1 for chest pain evaluation. Recommendations reviewed and implemented same day. Clear communication with consulting team documented. Follow-up arranged prior to discharge.[[/HIGHLIGHT]]' :
          '[[HIGHLIGHT]][COORDINATION GAP: Cardiology consult requested Day 1 but recommendations not reviewed until Day 3. Delay in implementing echo recommendation. Follow-up not arranged until prompted by attending.][[/HIGHLIGHT]]';
      }
      if (insightLower.includes('handoff') || insightLower.includes('transition')) {
        return isGood ?
          '[[HIGHLIGHT]]Transitions of Care: Structured handoff performed at each shift change. Pending items clearly communicated. Cross-coverage notes thorough. No issues with overnight care transitions.[[/HIGHLIGHT]]' :
          '[[HIGHLIGHT]][HANDOFF ISSUE: Pending lab results not communicated at shift change. Overnight team unaware of critical potassium recheck needed. Morning team discovered K 5.8 not addressed for 12 hours.][[/HIGHLIGHT]]';
      }
      
      // Generic fallback
      return isGood ?
        `[[HIGHLIGHT]]QUALITY INDICATOR - POSITIVE: ${insight.text}\nThis exemplary practice was consistently demonstrated throughout the hospitalization and positively impacted patient care.[[/HIGHLIGHT]]` :
        `[[HIGHLIGHT]][QUALITY INDICATOR - AREA FOR IMPROVEMENT: ${insight.text}]\nThis finding was identified during chart review and represents an opportunity for educational intervention and quality improvement.[[/HIGHLIGHT]]`;
    };
    
    const evidence = generateEvidence();
    
    // Map section to readable name for the analysis footer
    const sectionDisplayName: { [key in EvidenceSection]: string } = {
      'allergies': 'Allergies Section',
      'social_history': 'Social History',
      'physical_exam_neck': 'Physical Exam - Neck/Meningeal Signs',
      'physical_exam_neuro': 'Physical Exam - Neurological',
      'pertinent_results': 'Pertinent Results',
      'hospital_course': 'Brief Hospital Course',
      'discharge_instructions': 'Discharge Instructions'
    };
    
    return `Admission Date:  [**2124-${randomMonth}-${randomDate}**]              Discharge Date:   [**2124-${randomMonth}-${randomDate + 5}**]

Service: MEDICINE

Allergies:
${evidenceSection === 'allergies' ? evidence : 'Penicillin (rash) / Sulfa drugs (hives)'}

Attending:[**First Name3 (LF) ${Math.floor(Math.random() * 1000)}**]
Chief Complaint:
${insightLower.includes('fever') || insightLower.includes('meningeal') ? 'Fever and headache' : 
  insightLower.includes('cardio') || insightLower.includes('echo') || insightLower.includes('chest') ? 'Chest pain and shortness of breath' :
  insightLower.includes('respiratory') || insightLower.includes('copd') || insightLower.includes('pneumonia') ? 'Acute dyspnea and productive cough' :
  'Weakness and fatigue'}

Major Surgical or Invasive Procedure:
${insightLower.includes('cardio') ? 'Cardiac catheterization\nEchocardiogram' : 'Central line placement'}

History of Present Illness:
${generateHPI(insight)}

Past Medical History:
# Hypertension
# Type 2 Diabetes Mellitus  
# Hyperlipidemia
# Coronary Artery Disease
# GERD

Social History:
${evidenceSection === 'social_history' ? evidence : 
  'Lives with spouse. Retired teacher. Former smoker (quit 10 years ago, 20 pack-year history). Social alcohol use. Denies illicit drugs.'}

Family History:
Father with MI at age 55, Mother with Type 2 DM, Sister with breast cancer

Physical Exam:
On admission
Vitals: T: 101.2°F BP: 142/88 P: 98 RR: 22 O2: 94% on RA
General: Alert, oriented, no acute distress
HEENT: PERRL, EOMI, MMM, oropharynx clear
${evidenceSection === 'physical_exam_neck' ? evidence : 'Neck: Supple, no JVD, no lymphadenopathy'}
Lungs: Clear to auscultation bilaterally
CV: Regular rate and rhythm, normal S1/S2, no murmurs
Abdomen: Soft, non-tender, non-distended, normoactive bowel sounds
Ext: No edema, 2+ pulses bilaterally
${evidenceSection === 'physical_exam_neuro' ? evidence : 'Neuro: A&O x3, CN II-XII intact, strength 5/5 throughout, sensation intact'}

Pertinent Results:
[**2124-${randomMonth}-${randomDate}**] CBC: WBC 12.4, Hgb 13.2, Plt 245
[**2124-${randomMonth}-${randomDate}**] BMP: Na 138, K 4.2, Cl 102, HCO3 24, BUN 18, Cr 1.0, Glucose 156
[**2124-${randomMonth}-${randomDate}**] LFTs: Within normal limits
[**2124-${randomMonth}-${randomDate}**] Troponin: <0.01 x3
${evidenceSection === 'pertinent_results' ? '\n' + evidence + '\n' : ''}
CXR: No acute cardiopulmonary process
CT Head (if meningeal concerns): No acute intracranial abnormality

Brief Hospital Course:
======================
Patient was admitted to the medicine service for evaluation and management.

${evidenceSection === 'hospital_course' ? evidence + '\n\n' : ''}${isGood ? 
`${residentName} provided thorough and attentive care throughout the hospitalization. Patient was managed with appropriate medications and monitoring. Clinical status improved with treatment.` :
`During the hospitalization, the patient received standard care. ${evidenceSection !== 'hospital_course' ? 'Quality review findings are documented in the relevant section above.' : 'Quality review findings have been addressed with the care team.'}`}

Patient showed clinical improvement and was deemed safe for discharge on hospital day ${randomDate + 5 - randomDate}. Discharge planning was coordinated with appropriate follow-up arranged.

Medications on Admission:
1. Metformin 1000mg BID
2. Lisinopril 20mg daily
3. Atorvastatin 40mg daily
4. Omeprazole 20mg daily
5. Aspirin 81mg daily

Discharge Medications:
1. Metformin 1000mg BID
2. Lisinopril 20mg daily
3. Atorvastatin 40mg daily
4. Omeprazole 20mg daily
5. Aspirin 81mg daily

Discharge Disposition:
Home

Discharge Diagnosis:
Primary: ${insightLower.includes('fever') ? 'Fever, resolved' : 
          insightLower.includes('respiratory') ? 'Community-acquired pneumonia' :
          insightLower.includes('cardio') || insightLower.includes('chest') ? 'Chest pain, ruled out for ACS' : 'Acute illness, resolved'}
Secondary: Type 2 Diabetes Mellitus, Hypertension, Hyperlipidemia

Discharge Condition:
Mental Status: Clear and coherent
Level of Consciousness: Alert and interactive
Activity Status: Ambulatory - Independent

Discharge Instructions:
${evidenceSection === 'discharge_instructions' ? evidence : 
  'Standard discharge instructions provided. Return precautions reviewed. Follow-up appointments scheduled.'}

Follow-up Instructions:
- PCP: Dr. [**Last Name**] in 1 week
- ${insightLower.includes('cardio') ? 'Cardiology: Follow-up in 2 weeks' : 'Specialist follow-up as needed'}

---
Documented by: ${residentName}
Reviewed by: Attending Physician

═══════════════════════════════════════════════════════════════
SKILLSCOPE ANALYSIS
═══════════════════════════════════════════════════════════════
Finding: "${insight.text}"
Classification: ${isGood ? '✓ Good Practice' : '⚠ Area for Improvement'}
Skill Category: ${selectedSkill}
Evidence Location: ${sectionDisplayName[evidenceSection]}
═══════════════════════════════════════════════════════════════
`;
  };

  const generateHPI = (insight: Insight): string => {
    if (insight.text.includes('fever') || insight.text.includes('meningeal')) {
      return `68 year old female with history of Type 2 DM, HTN presents with 3 days of fever (Tmax 102.4°F at home), headache, and mild confusion per family. Patient reports gradual onset of symptoms with associated malaise and decreased oral intake. No recent travel, sick contacts, or new medications. No urinary symptoms, cough, or rash. Family notes patient has been "not herself" for the past day.

In the ED, patient was febrile to 101.8°F with mild tachycardia. Labs showed leukocytosis. CT head was negative for acute process. ${insight.type === 'bad' ? 'Initial examination was limited due to patient cooperation.' : 'Complete neurological examination was performed including assessment of meningeal signs.'}`;
    }
    
    if (insight.text.includes('cardio') || insight.text.includes('echo')) {
      return `72 year old male with history of CAD, prior MI (2018), HTN, HLD presents with 2 hours of substernal chest pressure radiating to left arm. Patient reports symptoms began at rest while watching television. Associated with diaphoresis and mild nausea. Took 3 sublingual nitroglycerin at home with minimal relief. ${insight.type === 'good' ? 'Previous cardiac workup reviewed including prior echocardiogram showing EF 50%.' : 'Previous cardiac testing history not documented.'}

In the ED, patient was hemodynamically stable. Initial troponin was negative. ECG showed no acute ST changes compared to prior.`;
    }
    
    return `65 year old patient with multiple comorbidities presenting with 5 days of progressive weakness and fatigue. Patient reports decreased appetite and mild dyspnea on exertion. No chest pain, palpitations, or syncope. No recent illness or sick contacts.

Initial evaluation in ED showed stable vital signs. Basic labs obtained and patient admitted for further workup.`;
  };

  // Function to render text with highlights
  const renderHighlightedText = (text: string, insightType: 'good' | 'bad') => {
    const parts = text.split(/\[\[HIGHLIGHT\]\]|\[\[\/HIGHLIGHT\]\]/);
    let isHighlighted = false;
    
    return parts.map((part, index) => {
      if (index % 2 === 1) {
        // This is a highlighted section
        return (
          <span 
            key={index} 
            className={`evidence-highlight ${insightType === 'good' ? 'highlight-good' : 'highlight-bad'}`}
          >
            <span className="highlight-marker">📍 EVIDENCE</span>
            {part}
          </span>
        );
      }
      return part;
    });
  };

  const handleInsightClick = (insight: Insight) => {
    setSelectedInsight(insight);
    setShowModal(true);
    generateMedicalRecord(insight);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedInsight(null);
    setGeneratedRecord('');
  };

  return (
    <div className="trends-section">
      <div className="trends-chart-container">
        <Line data={chartData} options={chartOptions} />
      </div>

      <h3 className="skill-title">{selectedSkill}</h3>

      {/* Insights Section - Between chart and skill buttons */}
      {currentInsights.length > 0 && (
        <div className="skill-insights">
          <h4 className="insights-title">
            Performance Insights ({getTimeframeLabel(timeFrame)})
            <span className="insights-hint">Click an insight to view source documentation</span>
          </h4>
          <div className="insights-grid">
            {currentInsights.map((insight, index) => (
              <div 
                key={index} 
                className={`insight-item ${insight.type === 'good' ? 'insight-good' : 'insight-bad'} clickable`}
                onClick={() => handleInsightClick(insight)}
                title="Click to view source medical record"
              >
                <span className="insight-icon">{insight.type === 'good' ? '✓' : '!'}</span>
                <span className="insight-text">{insight.text}</span>
                <span className="insight-timeframe">{insight.timeframe}</span>
                <span className="insight-source-icon">📋</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="skills-container">
        <div className="skills-grid">
          <div className="skills-column">
            {leftColumnSkills.map((skill) => (
              <button
                key={skill}
                className={`skill-button ${selectedSkill === skill ? 'active' : ''}`}
                onClick={() => setSelectedSkill(skill)}
              >
                {skill}
              </button>
            ))}
          </div>
          <div className="skills-column">
            {rightColumnSkills.map((skill) => (
              <button
                key={skill}
                className={`skill-button ${selectedSkill === skill ? 'active' : ''}`}
                onClick={() => setSelectedSkill(skill)}
              >
                {skill}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Timeframe-specific Evaluation Summary Section */}
      {summary && (
        <div className="evaluation-summary">
          <h3 className="summary-title">📋 {getTimeframeLabel(timeFrame)} Evaluation Summary</h3>
          
          <div className="summary-columns">
            <div className="summary-column learning-points">
              <h4>⚠️ Learning Points</h4>
              <ul>
                {summary.learningPoints.map((point, index) => (
                  <li key={index}>{point}</li>
                ))}
              </ul>
            </div>
            
            <div className="summary-column good-practice">
              <h4>✓ Good Practice</h4>
              <ul>
                {summary.goodPractice.map((point, index) => (
                  <li key={index}>{point}</li>
                ))}
              </ul>
            </div>
          </div>

          <div className="overall-assessment">
            <h4>Overall Assessment</h4>
            <p>{summary.overallSummary}</p>
          </div>

          <div className="exposure-recommendation">
            <h4>📊 Recommended Clinical Exposure</h4>
            <p>{summary.exposureRecommendation}</p>
          </div>
        </div>
      )}

      {/* Medical Record Modal */}
      {showModal && (
        <div className="medical-record-modal-overlay" onClick={closeModal}>
          <div className="medical-record-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                📋 Source Documentation
                <span className={`modal-badge ${selectedInsight?.type === 'good' ? 'badge-good' : 'badge-bad'}`}>
                  {selectedInsight?.type === 'good' ? '✓ Good Practice' : '⚠ Area for Improvement'}
                </span>
              </h3>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            <div className="modal-insight-summary">
              <strong>Insight:</strong> {selectedInsight?.text}
              <button 
                className="jump-to-evidence"
                onClick={() => {
                  const highlight = document.querySelector('.evidence-highlight');
                  if (highlight) {
                    highlight.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }
                }}
              >
                📍 Jump to Evidence
              </button>
            </div>
            <div className="modal-content">
              {isGenerating ? (
                <div className="generating-indicator">
                  <div className="spinner"></div>
                  <p>Generating medical admission record...</p>
                  <p className="generating-subtext">Analyzing documentation patterns</p>
                </div>
              ) : (
                <div className="medical-record-text">
                  {renderHighlightedText(generatedRecord, selectedInsight?.type || 'bad')}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <span className="modal-disclaimer">
                ⓘ This is AI-generated sample documentation for demonstration purposes
              </span>
              <button className="modal-close-btn" onClick={closeModal}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrendsSection;
